<template>
    <div>
        <header class="modal-card-head is-radiusless">
            <div class="level">
                <div class="level-left">
                    <div class="level-item">
                        <condo-icon icon="download" size="is-small" type="is-clink" />
                    </div>
                    <div class="level-item">
                        Download folder:
                        <h1 class="title is-6">{{ props.folder.folderName }}</h1>
                    </div>
                </div>
            </div>
        </header>
        <section class="modal-card-body">
            <n-input
                @keyup.enter="download"
                ref="inputRef"
                focus
                v-model:value="folderName"
                type="text"
                placeholder="Enter folder name"
                :id="buildId('foldername')"
            />
        </section>
        <footer class="modal-card-foot">
            <div class="is-flex is-justify-content-flex-end is-fullwidth">
                <condo-button @click="emit('close')" type="is-default" :id="buildId('close')"> Cancel </condo-button>
                <condo-button :disabled="!folderName.length" icon-left="save" @click="download" :outlined="false" type="is-primary" :id="buildId('download')">
                    Download
                </condo-button>
            </div>
        </footer>
    </div>
</template>

<script lang="ts" setup>
import { elementIdBuilder, showToast, ToastType, withLoading } from '@/shared';
import { NInput } from 'naive-ui';
import { onMounted, ref } from 'vue';
import { DocumentCategory, IDocumentFolder, IEstate, IMFH } from '@condo/domain';
import { scheduleDurableJob } from '@/api/durable-jobs.api';

interface Props {
    folder: IDocumentFolder;
    actionIdPrefix?: string;
    estate?: IEstate;
    mfh?: Pick<IMFH, 'mfhId'>;
    documentCategory?: DocumentCategory;
    documentType?: DocumentType;
}

type Emits = (e: 'close') => void;

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const getNamePrefixForEstate = (estate: IEstate) => {
    return `${estate.condoUuid ? estate.condoUuid : estate.estateId}_(${estate?.location?.street}_${estate?.location?.houseNumber})`;
};
const getNamePrefix = () => {
    if (props.estate) {
        return getNamePrefixForEstate(props.estate);
    }
    return '';
};
const getFolderName = () => {
    const namePrefix = getNamePrefix();
    return `${namePrefix ? `${namePrefix}_` : ''}${props.folder.folderName}`;
};

const inputRef = ref(null);
const folderName = ref<string>(getFolderName());

onMounted(() => inputRef.value?.focus());

const download = async () => {
    if (!folderName.value.length) {
        emit('close');
        return;
    }
    await withLoading(async () => {
        await scheduleDurableJob(
            {
                name: 'downloadDocumentFolder',
                params: {
                    folderId: props.folder.folderId,
                    folderName: folderName.value,
                    estateId: props.estate?.estateId,
                    documentCategory: props.documentCategory,
                    documentType: props.documentType,
                    mfhId: props.mfh?.mfhId,
                },
            },
            'basecamp',
        );
        showToast('Preparing your download... This may take a moment.', ToastType.INFO);
    });

    emit('close');
};

const buildId = (id: string) => (props.actionIdPrefix ? elementIdBuilder(props.actionIdPrefix)(id) : undefined);
</script>
